import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Upload, Sparkles, Gauge, MessagesSquare, Layers, ClipboardCheck,
    Target, GraduationCap, Trophy, ArrowRight, Check, Flame, AlertTriangle,
    Timer, Zap,
} from "lucide-react";
import { useTranslation } from "@/context/LanguageContext";

const NODE_META = [
    { key: "upload", icon: Upload },
    { key: "summary", icon: Sparkles },
    { key: "modes", icon: Gauge },
    { key: "chat", icon: MessagesSquare },
    { key: "flashcards", icon: Layers },
    { key: "quiz", icon: ClipboardCheck },
    { key: "exam", icon: Timer },
    { key: "gaps", icon: Target },
    { key: "coach", icon: GraduationCap },
    { key: "mastery", icon: Trophy },
];

export const usePrefersReducedMotion = () => {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const update = () => setReduced(mq.matches);
        update();
        mq.addEventListener?.("change", update);
        return () => mq.removeEventListener?.("change", update);
    }, []);
    return reduced;
};

const Ring = ({ value = 86, size = 58, stroke = 6 }) => {
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const off = c - (value / 100) * c;
    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="-rotate-90">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--surface-3))" strokeWidth={stroke} />
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--primary))" strokeWidth={stroke} strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-sans-display text-sm font-semibold text-t1">{value}</div>
        </div>
    );
};

const Mock = ({ k, t }) => {
    switch (k) {
        case "upload":
            return (
                <div className="space-y-3">
                    <div className="rounded-xl border border-dashed border-border bg-surface-1 p-4 text-center">
                        <Upload className="mx-auto h-5 w-5 text-[hsl(var(--primary))]" />
                        <div className="mt-2 text-xs text-t3">{t("preview.upload.drop")}</div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {["research-paper.pdf", "chapter-3.epub", "lecture-notes.docx"].map((f) => (
                            <span key={f} className="rounded-md border border-border bg-surface-1 px-2 py-1 text-[11px] text-t2">{f}</span>
                        ))}
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-1"><div className="h-full w-2/3 rounded-full bg-[hsl(var(--primary))]" /></div>
                </div>
            );
        case "summary":
            return (
                <div className="space-y-2.5">
                    <div className="mono-label text-t4">{t("preview.summary.label5")}</div>
                    {t("preview.summary.bullets").map((b, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-t2"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--success))]" />{b}</div>
                    ))}
                    <div className="flex flex-wrap gap-1.5 pt-1">{t("preview.summary.terms").map((tm) => <span key={tm} className="rounded-full border border-border bg-surface-1 px-2 py-0.5 text-[10px] text-t3">{tm}</span>)}</div>
                </div>
            );
        case "modes": {
            const modes = t("landing.readingModes.modes");
            const icons = [Zap, Gauge, Target];
            return (
                <div className="space-y-2">
                    <div className="mono-label text-t4">{t("landing.readingModes.label")}</div>
                    {modes.map((m, idx) => {
                        const Ic = icons[idx] || Gauge;
                        return (
                            <div key={m.name} className="flex items-center gap-2.5 rounded-xl border border-border bg-surface-1 px-3 py-2 text-xs">
                                <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]"><Ic className="h-3.5 w-3.5" /></span>
                                <span className="font-medium text-t1">{m.name}</span>
                            </div>
                        );
                    })}
                </div>
            );
        }
        case "chat":
            return (
                <div className="space-y-2">
                    <div className="ml-auto max-w-[85%] rounded-2xl border border-border bg-surface-1 px-3 py-2 text-xs text-t1">{t("preview.chat.q1")}</div>
                    <div className="mr-auto max-w-[92%] rounded-2xl border border-border bg-surface-1 px-3 py-2 text-xs text-t2">{t("preview.chat.a1")} <span className="ml-1 rounded-full border border-border bg-surface-2 px-1.5 py-0.5 text-[10px] text-t3">p. 12</span></div>
                </div>
            );
        case "flashcards":
            return (
                <div className="rounded-xl border border-border bg-surface-1 p-5 text-center">
                    <div className="mono-label text-t4">{t("preview.flashcard.question")}</div>
                    <div className="mt-2 font-reading text-sm text-t1">{t("preview.flashcard.q")}</div>
                    <div className="mt-3 text-[11px] text-[hsl(var(--primary))]">{t("preview.flashcard.activeRecall")}</div>
                </div>
            );
        case "quiz": {
            const opts = t("preview.quiz.options");
            return (
                <div className="space-y-2">
                    <div className="text-xs font-medium text-t1">{t("preview.quiz.q")}</div>
                    {opts.map((o, i) => {
                        const ok = i === 1;
                        return (
                            <div key={o} className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs ${ok ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-t1" : "border-border bg-surface-1 text-t2"}`}>
                                <span className={`h-3.5 w-3.5 rounded-full border ${ok ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]" : "border-border"}`} />{o}
                            </div>
                        );
                    })}
                </div>
            );
        }
        case "exam":
            return (
                <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                        <span className="mono-label text-t4">{t("preview.exam.mock")}</span>
                        <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-2 py-0.5 text-[10px] text-[hsl(var(--warning))]"><Timer className="h-3 w-3" /> 12:48</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-1"><div className="h-full w-1/5 rounded-full bg-[hsl(var(--primary))]" /></div>
                    <div className="text-xs font-medium text-t1">{t("preview.exam.prompt")}</div>
                    <div className="flex items-center justify-between pt-1">
                        <span className="mono-label text-t4">{t("preview.exam.q")}</span>
                        <span className="rounded-md bg-[hsl(var(--primary))] px-2 py-1 text-[10px] font-medium text-[hsl(var(--primary-foreground))]">{t("preview.exam.submit")}</span>
                    </div>
                </div>
            );
        case "gaps": {
            const topics = t("preview.gaps.topics");
            const vals = [38, 55];
            return (
                <div className="space-y-2.5">
                    <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--warning))]"><AlertTriangle className="h-3.5 w-3.5" /> 2 {t("preview.gaps.need")}</div>
                    {topics.map((tp, i) => (
                        <div key={tp}>
                            <div className="mb-1 flex justify-between text-[11px] text-t3"><span>{tp}</span><span>{vals[i]}%</span></div>
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-1"><div className="h-full rounded-full bg-[hsl(var(--warning))]" style={{ width: `${vals[i]}%` }} /></div>
                        </div>
                    ))}
                </div>
            );
        }
        case "coach":
            return (
                <div className="space-y-2.5">
                    <div className="flex items-center gap-2 text-xs font-medium text-t1"><GraduationCap className="h-4 w-4 text-[hsl(var(--primary))]" /> {t("preview.coach.todayPlan")}</div>
                    {t("preview.coach.items").map((it) => (
                        <div key={it} className="flex items-center gap-2 rounded-lg border border-border bg-surface-1 px-3 py-1.5 text-xs text-t2"><Check className="h-3.5 w-3.5 text-[hsl(var(--success))]" />{it}</div>
                    ))}
                </div>
            );
        case "mastery":
            return (
                <div className="flex items-center gap-4 py-1">
                    <Ring value={86} />
                    <div className="space-y-1">
                        <div className="flex items-center gap-1.5 text-xs text-t1"><Flame className="h-4 w-4 text-[hsl(var(--warning))]" /> {t("preview.mastery.streak")}</div>
                        <div className="text-xs text-t3">{t("preview.mastery.mastered")}</div>
                        <div className="text-xs text-t3">{t("preview.mastery.retention")}</div>
                    </div>
                </div>
            );
        default:
            return null;
    }
};

export const AnimatedJourney = () => {
    const { t } = useTranslation();
    const reduced = usePrefersReducedMotion();
    const [i, setI] = useState(0);

    useEffect(() => {
        if (reduced) return undefined;
        const id = setInterval(() => setI((v) => (v + 1) % NODE_META.length), 2200);
        return () => clearInterval(id);
    }, [reduced]);

    const labels = t("landing.journey");
    const active = NODE_META[i];
    const ActiveIcon = active.icon;

    return (
        <div data-testid="hero-journey-canvas" className="relative overflow-hidden rounded-[var(--radius)] border border-border bg-surface-1 shadow-elev-2 noise-overlay">
            <div className="relative p-4 sm:p-5">
                {/* node rail */}
                <div className="flex flex-wrap gap-2">
                    {NODE_META.map((n, idx) => {
                        const on = idx === i;
                        const Icon = n.icon;
                        return (
                            <button
                                key={n.key}
                                type="button"
                                onClick={() => setI(idx)}
                                data-testid={`hero-journey-node-${n.key}`}
                                aria-label={labels[idx]}
                                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--focus))] ${on ? "border-[hsl(var(--primary))]/55 bg-[hsl(var(--primary))]/10 text-t1" : "border-border bg-surface-2 text-t3 hover:text-t1"}`}
                            >
                                <Icon className="h-3.5 w-3.5" style={{ color: on ? "hsl(var(--primary))" : undefined }} />
                                {labels[idx]}
                            </button>
                        );
                    })}
                </div>

                {/* progress line */}
                <div className="mt-4 h-1 w-full overflow-hidden rounded-full bg-surface-2">
                    <motion.div className="h-full rounded-full bg-[hsl(var(--primary))]" animate={{ width: `${((i + 1) / NODE_META.length) * 100}%` }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} />
                </div>

                {/* preview */}
                <div className="mt-4">
                    <div className="mb-2 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm font-medium text-t1"><ActiveIcon className="h-4 w-4 text-[hsl(var(--primary))]" />{labels[i]}</div>
                        <span className="mono-label text-t4">{t("preview.step")} {i + 1}/{NODE_META.length}</span>
                    </div>
                    <div className="relative min-h-[156px] rounded-[calc(var(--radius)-2px)] border border-border bg-surface-2 p-4 shadow-elev-1">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active.key}
                                initial={reduced ? false : { opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={reduced ? { opacity: 0 } : { opacity: 0, y: -8 }}
                                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <Mock k={active.key} t={t} />
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>

                {/* footer caption */}
                <div className="mt-4 flex items-center gap-2 text-xs text-t3">
                    <span className="inline-flex items-center gap-1"><Upload className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> {labels[0]}</span>
                    <ArrowRight className="h-3 w-3 text-t4" />
                    <span className="inline-flex items-center gap-1"><Trophy className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> {labels[NODE_META.length - 1]}</span>
                    <span className="ml-auto mono-label text-t4">{t("preview.onePlatform")}</span>
                </div>
            </div>
        </div>
    );
};
